/**
 * G2eGente - Utilitários de Formatação
 * Funções para formatação de datas, números, etc.
 */

/**
 * Formata uma data para o formato brasileiro DD/MM/AAAA
 * @param {string|Date} date - Data no formato ISO (YYYY-MM-DD) ou objeto Date
 * @returns {string} Data formatada ou string vazia se inválida
 */
export function formatDateBR(date) {
  if (!date) return '';
  
  try {
    // Se for string no formato ISO (YYYY-MM-DD ou YYYY-MM-DDTHH:mm:ss)
    if (typeof date === 'string') {
      // Extrai apenas a parte da data se tiver datetime
      const datePart = date.split('T')[0];
      const parts = datePart.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
      }
    }
    
    // Se for objeto Date
    if (date instanceof Date && !isNaN(date)) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    
    // Tenta criar um Date a partir da string
    const d = new Date(date);
    if (!isNaN(d)) {
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    }
    
    return '';
  } catch {
    return '';
  }
}

/**
 * Formata uma data com hora para o formato brasileiro DD/MM/AAAA HH:mm
 * @param {string|Date} date - Data no formato ISO ou objeto Date
 * @returns {string} Data e hora formatadas ou string vazia se inválida
 */
export function formatDateTimeBR(date) {
  if (!date) return '';
  
  try {
    const d = new Date(date);
    if (isNaN(d)) return '';
    
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '';
  }
}

/**
 * Converte data do formato BR (DD/MM/AAAA) para ISO (YYYY-MM-DD)
 * @param {string} dateBR - Data no formato DD/MM/AAAA
 * @returns {string} Data no formato YYYY-MM-DD ou string vazia se inválida
 */
export function parseDateBRtoISO(dateBR) {
  if (!dateBR) return '';
  
  try {
    const parts = dateBR.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return '';
  } catch {
    return '';
  }
}

/**
 * Formata CPF: 000.000.000-00
 * @param {string} cpf - CPF sem formatação
 * @returns {string} CPF formatado
 */
export function formatCPF(cpf) {
  if (!cpf) return '';
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length !== 11) return cpf;
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Formata CNPJ: 00.000.000/0000-00
 * @param {string} cnpj - CNPJ sem formatação
 * @returns {string} CNPJ formatado
 */
export function formatCNPJ(cnpj) {
  if (!cnpj) return '';
  const cleaned = cnpj.replace(/\D/g, '');
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

/**
 * Formata telefone: (00) 00000-0000 ou (00) 0000-0000
 * @param {string} phone - Telefone sem formatação
 * @returns {string} Telefone formatado
 */
export function formatPhone(phone) {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return phone;
}

/**
 * Formata valor monetário em Real brasileiro
 * @param {number} value - Valor numérico
 * @returns {string} Valor formatado (R$ 1.234,56)
 */
export function formatCurrency(value) {
  if (value === null || value === undefined) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}

/**
 * Formata número com separador de milhar brasileiro
 * @param {number} value - Valor numérico
 * @returns {string} Valor formatado (1.234,56)
 */
export function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined) return '';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
}
